import { Directive, ElementRef, EventEmitter, HostListener, Injectable, Input, OnInit, Optional, Output } from '@angular/core';
import { NgControl } from '@angular/forms';

/* Makes an Input field a Scanner Input field
 * Blocks use of physical and virtual keyboards by default
 *      (unless inputmode='decimal', inputmode='numeric' or attribute scanallowkeyboard is specified)
 * Handles JSON-QR-code scans populating JSON values into tagged fields in the same form or fieldset
 *
 * Prevent use of keyboards in Input field
 *      <input scanInput />
 *
 * Allow use of physical keyboard for numeric / quantity fields while also accepting "QTY" from QR scan
 *      <input scanInput="QTY" inputmode="decimal" />
 *
 * JSON-QR-Codes - When any scanInput in the form receives a JSON-QR scan
 * all fields within the form or fieldset mapped to JSON keys receive their respective value
 *  Set scanInput to the JSON object key the field should receive the value from
 *  Add event handler for (scanned) where $event will be the value for that key from the JSON-QR
 *  Or bind through [(ngModel)]
 *      <input scanInput="ITM" (scanned)="receiveItem($event: string | number | null)" />
 *      <input scanInput="LOT" (scanned)="lotserNbr = $event" />
 *      <input scanInput="QTY" [(ngModel)]="quantity" />
 *      <input scanInput="*" (scanned)="userScanInput($event: any)" />
 */

@Directive({
    selector: '[scanInput]'
})
export class ScanInputDirective implements OnInit {
    public element: any;
    private _numericInput: boolean;
    private _decimalInput: boolean;
    private allowKeyboard = false;

    /** an attribute attached to an input element specifying it is a field expected to receive scanner inputs
     * assigning a value to the attribute specifies the label from JSON QR data the input receives
     * [scanInput]="LOT" will receive the value of "LOT" when scanning a QR containing one {"ITM":"123"," "LOT":"ABC"}
     */
    @Input() scanInput: string | undefined = '';

    /** an event that is fired when a value is entered into the field providing the scanned value */
    @Output() scanned = new EventEmitter<string | number | null>();

    /** specifies a field to move focus to automatically when a value is scanned into this field */
    @Input() nextQR: HTMLElement | null = null;

    // by default, any [scanInput] attributed field does not allow the virtual keyboard in WMS
    // public allowKeyboard: boolean = (environment.mode != 'wms');

    constructor(
        private elementRef: ElementRef,
        private scanMgr: ScannerManagerService,
        @Optional() public ngControl?: NgControl
    ) {
        this.element = this.elementRef.nativeElement;

        let inputMode = this.element.getAttribute('inputmode')?.toLowerCase();
        this._numericInput = (inputMode == 'numeric');
        this._decimalInput = (inputMode == 'decimal');

        /*
        inputmode='none' while functionally prevents virtual keyboard from appearing,
        upon scanning a barcode, the MunByn scanners failed to populate the scanned data
        into the input field -- sometimes dropping chunks of the input string, or transposing various chunks
        seemingly in relation to some keystrokes entered by the barcode scanner causing the virtual keyboard
        to attempt to display and those characters being 'captured' by the virtual keyboard, while the scanner
        continues to enter characters into the input, before the 'captured' characters are then populated in
        */

        // scanallowkeyboard attribute is specified, virtual keyboard explicitly allowed
        // (can use [scanInput]="ITM" scanallowkeyboard to received QR:ITM scans OR manually type item #)
        if (this.element.hasAttribute('scanallowkeyboard')) this.allowKeyboard = true;
        // allowKeyboard everywhere setting turned on
        else if (scanMgr.allowKeyboard) this.allowKeyboard = true;
        // allowNumericKeyboard setting turned on
        else if ((this._numericInput || this._decimalInput)
            && this.scanMgr.allowNumericKeyboard) this.allowKeyboard = true;

        // when no virtual keyboard allowed, set inputmode='none'
        if (!this.allowKeyboard) this.element.setAttribute('inputmode', 'none');
    }

    ngOnInit(): void {
        if (!this.allowKeyboard) this.element.setAttribute('inputmode', 'none');
        this.scanMgr.RegisterScannerInputField(this);
    }

    // /*
    // when a user is trying to type into a scanInput field the keys will come in slowly over time
    // when the barcode scanner is entering data they'll happen in quick succession
    // a timer is used and reset on keypress
    // if a value is entered into the input and [Enter] pressed before the timer has elapsed, input is coming from a scanner
    // otherwise, it's coming from a user and should be cleared
    // */
    // keypressIntervalTimer?: any;
    // @HostListener('keydown', ['$event'])
    // onKeyDown($event: KeyboardEvent) {
    //     if (
    //         // keyboard is allowed in this field
    //         this.allowKeyboard
    //         // or this is a numeric field
    //         || this._decimalInput
    //         // or the input is the physical scanner button
    //         || $event.key.toLowerCase() == 'unidentified'
    //     )
    //         // allow the event
    //         return;

    //     // reset the timer with each keypress
    //     if (this.keypressIntervalTimer) clearTimeout(this.keypressIntervalTimer);

    //     // if the interval elapses before another key press, clear and reset the field
    //     this.keypressIntervalTimer = setTimeout(() => {
    //         // unless we're entering a JSON QR scan value or the field is already blank
    //         if (this.element.value.substr(0, 1) == '{') return;
    //         if (this.element.value == '') return;

    //         this.element.value = '';
    //         this.element.blur();
    //         this.setValue('');
    //         setTimeout(() => { this.element.focus(); }, 0);
    //     }, 100);
    // }

    @HostListener('keyup.enter', ['$event']) onEnterKey(event: KeyboardEvent) {
        let value = this.element.value.trim().toUpperCase();
        let isJSON = false;

        // when scanned JSON QR code
        if (value.substr(0, 1) == "{" && value.substr(value.length - 1, 1) == "}") {
            try {
                let data = JSON.parse(value);
                isJSON = true;
                this.scanMgr.SetScannerInputFields(this, data);
            }
            catch { }
        }

        // if the field that was qr-scanned into hasn't been changed (not included in QR field content)
        if (isJSON && value == this.element.value.trim().toUpperCase()) {
            this.setValue(null);
        }
        // scanned / entered non-JSON value (legacy 1D barcode) -- trigger (scanned) event with current value
        if (!isJSON) {
            this.setValue(value);
        }

        // if a scanned value was received into this field, focus the next input
        if (value) {
            let target = this.element;
            if (this.nextQR) {
                target = this.nextQR;
            } else {
                let currentTargetIndex: number | undefined = undefined;
                let inputs = target.closest('form,fieldset').querySelectorAll('input,select,button[type="submit"]');
                inputs.forEach((value: Node, key: number) => { if (value == target) currentTargetIndex = key });
                if (currentTargetIndex !== undefined) target = inputs[currentTargetIndex + 1] || target;
            }

            target.focus();
            if (target.nodeName == 'BUTTON') {
                target.click();
            } else {
                target.select();
            }
        }

        event.preventDefault();
    };

    // switching focus from a numeric field into a scanInput field
    // virtual keyboard layouts changes and keeps the softkeyboard open
    keyboardFocusClosed: boolean = false;
    @HostListener('focus')
    onFocus() {
        if (this.keyboardFocusClosed || this.allowKeyboard) return;
        this.element.setAttribute('readonly', 'readonly'); // force keyboard to hide on input field
        this.element.setAttribute('disabled', 'true'); // force keyboard to hide on textarea field
        setTimeout(() => {
            this.element.blur(); // actually close the keyboard
            this.element.removeAttribute('readonly');
            this.element.removeAttribute('disabled');
            this.element.focus();
        });
        this.keyboardFocusClosed = true;
    }

    @HostListener('blur') onBlur(event: any) {
        this.keyboardFocusClosed = false;
        let val: any = undefined;
        if (this._decimalInput)
            val = parseFloat(this.element.value);
        else if (this._numericInput)
            val = parseInt(this.element.value);
        else
            val = this.element.value.toString().toUpperCase();

        if (val !== undefined) {
            val = (Number.isNaN(val)) ? null : val;
            if (val != this.element.value) {
                this.setValue(val);
            }
        }
    }

    // set value to scanInput field
    public setValue(value: string | number | null, emitEvents = true): (() => void) | undefined {
        let isObject = value != null && typeof value == 'object';
        if (!value) return;

        if (!isObject) {
            // when using [(ngModel)]="boundField" or formControlName=".." => FormControl()
            if (this.ngControl) {
                this.ngControl.control?.setValue(value);
                this.ngControl.viewToModelUpdate(value);
            } else {
                // set DOM input field value when not using ngModel
                this.element.value = value;
            }
        }

        // function to emit events
        let eventsEmitter = () => {
            // emit (scanned) and (change) events
            this.scanned.emit(value);
            this.element.dispatchEvent(new Event('change'));
        }

        // run the emit events function now or return it to the caller
        if (emitEvents) {
            eventsEmitter();
            return;
        } else {
            return eventsEmitter;
        }
    }
}

// @Directive({
//     selector: 'input[scanInput2], textarea[scanInput2]'
// })
// export class ScanInputV2Directive implements OnInit {
//     public element: any;

//     /** an attribute attached to an input element specifying it is a field expected to receive scanner inputs
//      * assigning a value to the attribute specifies the label from JSON QR data the input receives
//      * [scanInput]="LOT" will receive the value of "LOT" when scanning a QR containing one {"ITM":"123"," "LOT":"ABC"} */
//     @Input() scanInput2: string | undefined = '';

//     /** an event that is fired when a value is entered into the field providing the received value */
//     @Output() scanned = new EventEmitter<string>();
//     private _allowKeyboard: boolean;

//     constructor(
//         private elementRef: ElementRef,
//         private scanMgr: ScannerManagerService,
//         @Optional() public ngControl?: NgControl
//     ) {
//         this.element = this.elementRef.nativeElement;
//         this._allowKeyboard = this.element.hasAttribute('allowKeyboard') || this.scanMgr.allowKeyboard;
//     }

//     ngOnInit(): void {
//         if (!this._allowKeyboard) this.element.setAttribute('inputmode', 'none');
//         this.scanMgr.RegisterScannerInputField2(this);
//     }

//     @HostListener('keyup.enter', ['$event']) onEnterKey(event: KeyboardEvent) {
//         this.processScanInput();
//         event.preventDefault();
//     }

//     private processScanInput() {
//         let value: string = this.element.value;
//         let isJSON: boolean = false;

//         // when scanned JSON QR code
//         if (value.slice(0, 1) == "{" && value.slice(-1) == "}") {
//             try {
//                 let data = JSON.parse(value);
//                 isJSON = true;
//                 this.scanMgr.SetScannerInputFields2(this, data);
//             }
//             catch { }
//         }

//         // scanned / entered non-JSON value (legacy 1D barcode) -- trigger (scanned) event with current value
//         if (!isJSON) {
//             this.setValue(value);
//         }

//         // if a scanned value was received into this field, focus the next input
//         if (value) {
//             let target = this.element;

//             let currentTargetIndex: number | undefined = undefined;
//             // let inputs = target.closest('form,fieldset').querySelectorAll('input,select,button[type="submit"]');
//             let inputs = target.closest('form,fieldset').querySelectorAll('input,select');
//             inputs.forEach((value: Node, key: number) => { if (value == target) currentTargetIndex = key });
//             if (currentTargetIndex !== undefined) target = inputs[currentTargetIndex + 1] || target;

//             target.focus();
//             // if (target.nodeName == 'BUTTON') {
//             //     target.click();
//             // } else {
//             target.select();
//             // }
//         }
//     }

//     public setValue(value: string, emitEvents = true): (() => void) | undefined {
//         let isObject = value != null && typeof value == 'object';
//         if (!value) return;

//         if (!isObject) {
//             // when using [(ngModel)]="boundField" or formControlName=".." => FormControl()
//             if (this.ngControl) {
//                 this.ngControl.control?.setValue(value);
//                 this.ngControl.viewToModelUpdate(value);
//             } else {
//                 // set DOM input field value when not using ngModel
//                 this.element.value = value;
//             }
//         }

//         // function to emit (scanned) and (change) events
//         let eventsEmitter = () => {
//             this.scanned.emit(value);
//             this.element.dispatchEvent(new Event('change'));
//         }

//         // run the emit events function now or return it to the caller
//         if (emitEvents) {
//             eventsEmitter();
//             return;
//         } else {
//             return eventsEmitter;
//         }
//     }
// }

// @Directive({
//     selector: '[inputmode]'
// })
// export class InputModeDirective implements OnInit {
//     @Input() inputMode?: string;
//     constructor(
//         private el: ElementRef<HTMLInputElement>,
//         private scanMgr: ScannerManagerService
//     ) {
//         this.inputMode = el.nativeElement.getAttribute('inputmode') || undefined;
//     }

//     ngOnInit(): void {
//         if ((this.inputMode == 'numeric' || this.inputMode == 'decimal' || this.inputMode == 'quantity')
//             && environment.mode == 'wms'
//             && !this.scanMgr.allowNumericKeyboard)
//             this.el.nativeElement.setAttribute('inputmode', 'none');
//     }
// }

@Injectable({
    providedIn: 'root'
})
export class ScannerManagerService {
    get allowKeyboard(): boolean { return localStorage.getItem('allow-keyboard') == 'yes'; }
    set allowKeyboard(value) {
        if (value) localStorage.setItem('allow-keyboard', 'yes');
        else localStorage.removeItem('allow-keyboard');
    }

    get allowNumericKeyboard(): boolean { return localStorage.getItem('allow-decimal-keyboard') == 'yes'; }
    set allowNumericKeyboard(value) {
        if (value) localStorage.setItem('allow-decimal-keyboard', 'yes');
        else localStorage.removeItem('allow-decimal-keyboard');
    }

    // collection of container elements for scanner fields (the <form> elements that contain the set of scanInput fields)
    private ScannerInputContainers: any[] = [];
    // an array of scanInput fields within the corresponding parent container of the same array-index
    private ScannerInputFields: any[] = [];

    // Registers the field the directive is attached to within the container maps
    RegisterScannerInputField(directive: ScanInputDirective) {
        let element = directive.element;
        // get the scanInput='..' key value
        let key = element.getAttribute('scanInput');
        // if there isn't one, no need to record a mapping for the field
        if (!key) return;
        // get the form parent element
        let form = this.findScanContainer(element);
        // find that form within the array collection
        let index = this.ScannerInputContainers.indexOf(form);
        // first input for this form, create a new entry and collection
        if (index == -1) {
            index = this.ScannerInputContainers.push(form) - 1;
            this.ScannerInputFields[index] = {};
        }
        // record the element under it's appropriate field key
        this.ScannerInputFields[index][key] = directive;
    }

    // // Registers the field the directive is attached to within the container maps
    // RegisterScannerInputField2(directive: ScanInputV2Directive) {
    //     let element = directive.element;
    //     // get the scanInput='..' key value
    //     let key = element.getAttribute('scanInput2');
    //     // if there isn't one, no need to record a mapping for the field
    //     if (!key) return;
    //     // get the form parent element
    //     let form = this.findScanContainer(element);
    //     // find that form within the array collection
    //     let index = this.ScannerInputContainers.indexOf(form);
    //     // first input for this form, create a new entry and collection
    //     if (index == -1) {
    //         index = this.ScannerInputContainers.push(form) - 1;
    //         this.ScannerInputFields[index] = {};
    //     }
    //     // record the element under it's appropriate field key
    //     this.ScannerInputFields[index][key] = directive;
    // }

    // Populate {data} values into mapped scanInput fields
    SetScannerInputFields(directive: ScanInputDirective, data: any) {
        // reference to the scanInput container
        let parent = this.findScanContainer(directive.element);
        // index of the parent within its array
        let containerIndex = this.ScannerInputContainers.indexOf(parent);
        if (containerIndex == -1) return;

        // iterate fields within that parent collection
        let eventEmitters: (() => void)[] = [];
        let container = this.ScannerInputFields[containerIndex];
        Object.keys(data).forEach((key: string) => {
            // set value to all fields but do not emit (scanned) events until after all are updated
            let directive = (<ScanInputDirective>container[key]);
            if (directive) {
                let emitter = directive.setValue(data[key], false)!;
                eventEmitters.push(emitter);
            }
        });
        // any scanInput="*" receivers
        if (directive = container['*']) {
            let emitter = directive.setValue(data, false)!;
            eventEmitters.push(emitter);
        }

        // after all values have been set, then emit the events
        eventEmitters.forEach(emitter => emitter());
    }

    // // Populate {data} values into mapped scanInput fields
    // SetScannerInputFields2(directive: ScanInputV2Directive, data: any) {
    //     // index of the parent within its array
    //     let containerIndex = this.ScannerInputContainers.indexOf(this.findScanContainer(directive.element));
    //     if (containerIndex == -1) return;

    //     // initialize array list of eventEmitter functions
    //     let eventEmitters: (() => void)[] = [];
    //     let container = this.ScannerInputFields[containerIndex];
    //     // iterate fields within that container
    //     Object.keys(data).forEach((key: string) => {
    //         // set value to all fields but do not emit (scanned) events until after all are updated
    //         let directive = (<ScanInputDirective>container[key]);
    //         if (directive) {
    //             let emitter = directive.setValue(data[key], false)!;
    //             eventEmitters.push(emitter);
    //         }
    //     });
    //     // any scanInput="*" receivers
    //     if (directive = container['*']) {
    //         let emitter = directive.setValue(data, false)!;
    //         eventEmitters.push(emitter);
    //     }

    //     // after all values have been set, then emit the events
    //     eventEmitters.forEach(emitter => emitter());
    // }

    private findScanContainer(element: any): any {
        while (true) {
            if (element.nodeName.toUpperCase() == 'FORM' || element.nodeName.toUpperCase() == 'FIELDSET') return element;
            element = element.parentElement;
            if (!element) throw new Error("Scanner Input fields must be contained within a &lt;form&gt; or &lt;fieldset&gt;");
        }
    }
}